import { getCountryFormDefinition } from "../cmsUtils";
import { hasStageDimensions } from "../companyUtils";
import { findCountryById, findStateById } from "../countrystateutils";
import { findEnumObject } from "../enumutils";
import { parseFromTime } from "../momentutils";
import { createFieldMetaData, createFinancialFieldMetaData } from "./createFieldMetaData";
import type { FieldMetaData, FinancialFieldMetaData } from "./fieldsMetaData";

export interface DoorsFormState {
  open: moment.Moment;
  close: moment.Moment;
}

export interface NameAndAddress extends Address {
  companyName: string;
}

export interface Address {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  state: server.dto.State;
  country: server.dto.Country;
  dto: server.dto.Address;
}

export interface PostalAndVisitingAddress {
  visitingAddress: NameAndAddress;
  postalAddress: NameAndAddress;
}

export interface CompanyProfile {
  isArtist: server.dto.IsArtistTypes;
  isPromoter: server.dto.IsPromoterTypes;
  isVenue: server.dto.IsVenueTypes;
}

export interface CompanyFormState {
  isDeleted: boolean;
  isValidated: boolean;
  companyId: number;
  clientId: number;
  name: string;
  admName: string;
  businessCentralNumber: string;
  hubSpotCompanyId: number;

  address: PostalAndVisitingAddress;
  profile: CompanyProfile;
  status: server.dto.StatusSmall;
  tags: server.dto.TagSmall[];
  ianaTimeZoneId: string;
  website: string;
  phone: string;
  facebook: string;
  networkPath: string;
  additional: string;
  financialRisk: server.dto.FinancialRisk;
  euVatNumber: server.dto.EuVatNumber;
  globalLocationNumber: string;
  fiscalNumber1: string;
  fiscalNumber2: string;
  businessRegistrationNumber: string;
  owner: server.dto.UserSmall
  internalId: string;
  legalStructure: string;
  expectPurchaseOrderNumber: boolean;

  geoCode: server.dto.GeoCode;
  googlePlaceIdentifier: string;
  industryClassification: string;
}

export interface VenueFormState {
  capacity: number;
  ageRestriction: server.dto.AgeRestriction;
  doors: DoorsFormState;
  category: server.dto.VenueCategory;
  stage: server.dto.Stage
  vehicleAccess: string;
  parking: string;
  gearAccess: string;
  power: string;
  lighting: string;
  dressingRooms: string;
  noiseRestrictions: string;
}

export interface BankFormState {
  accountNumber: string;
  beneficiaryName: string;
  swiftCode: string;
  sortCode: string;
  bankAddress: string;
}

export const convertToPhoneNumbers = (value: string): string[] => {
  return value.split(/,|;/).map(num => num.trim()).filter(num => num.length > 0)
}

export const convertToNameAndAddress = (companyName: string, address: server.dto.Address, serverReference: server.dto.ServerReference): NameAndAddress => ({
  companyName: companyName ?? "",
  addressLine1: address.addressLine1 ?? "",
  addressLine2: address.addressLine2 ?? "",
  city: address.city ?? "",
  postalCode: address.postalCode ?? "",
  // @ts-ignore
  state: findStateById(address.stateId, serverReference),
  // @ts-ignore
  country: findCountryById(address.country, serverReference),
  dto: address,
});

export const convertToFormState = (company: server.dto.Company, serverReference: server.dto.ServerReference): CompanyFormState => ({
  isDeleted: company.isDeleted,
  isValidated: company.isValidated,
  companyId: company.companyId,
  clientId: company.clientId,
  name: company.name ?? "",
  admName: company.admName ?? "",

  // @ts-ignore .
  businessCentralNumber: company.businessCentralNumber ?? undefined,
  // @ts-ignore .
  hubSpotCompanyId: company.hubSpotCompanyId ?? undefined,

  address: {
    visitingAddress: convertToNameAndAddress(company.name, company.address, serverReference),
    postalAddress: convertToNameAndAddress(company.admName, company.admAddress, serverReference),
  },
  profile: {
    isArtist: company.isArtist,
    isPromoter: company.isPromoter,
    isVenue: company.isVenue,
  },
  status: company.status,
  tags: company.tags,
  ianaTimeZoneId: company.ianaTimeZoneId,
  website: company.website ?? "",
  phone: company.landlineNumbers.join(";"),
  facebook: company.facebook ?? "",
  networkPath: company.networkPath ?? "",
  additional: company.additional ?? "",
  financialRisk: company.financialRisk ?? {
    comment: "",
    level: "unknown",
  },
  euVatNumber: company.euVatNumber,
  fiscalNumber1: company.fiscalNumber1 ?? "",
  fiscalNumber2: company.fiscalNumber2 ?? "",
  businessRegistrationNumber: company.businessRegistrationNumber ?? "",
  owner: company.owner,
  internalId: company.internalId ?? "",
  legalStructure: company.legalStructure ?? "",
  expectPurchaseOrderNumber: company.expectPurchaseOrderNumber,
  globalLocationNumber: company.globalLocationNumber ?? "",

  geoCode: company.geoCode,
  googlePlaceIdentifier: company.googlePlaceIdentifier ?? "",
  industryClassification: company.industryClassification ?? "",
});

export const convertToVenueFormState = (
    venueDetails: server.dto.VenueDetails,
    serverReference: server.dto.ServerReference,
): VenueFormState => ({
  capacity: venueDetails?.capacity,
  // @ts-ignore
  ageRestriction: venueDetails?.ageRestriction ? findEnumObject(venueDetails.ageRestriction, serverReference.ageRestrictions) : findEnumObject("unknown", serverReference.ageRestrictions),
  doors: {
    // @ts-ignore
    open: venueDetails?.doors?.open ? parseFromTime(venueDetails.doors.open) : null,
    // @ts-ignore
    close: venueDetails?.doors?.close ? parseFromTime(venueDetails.doors.close) : null,
  },
  // @ts-ignore
  category: venueDetails?.category ? findEnumObject(venueDetails.category, serverReference.venueCategories) : findEnumObject("unknown", serverReference.venueCategories),
  stage: {
    // @ts-ignore
    width: venueDetails?.stage?.width,
    // @ts-ignore
    depth: venueDetails?.stage?.depth,
    // @ts-ignore
    toFloor: venueDetails?.stage?.toFloor,
    // @ts-ignore
    toCeiling: venueDetails?.stage?.toCeiling,
    formatted: []
  },
  vehicleAccess: venueDetails?.vehicleAccess ?? "",
  parking: venueDetails?.parking ?? "",
  gearAccess: venueDetails?.gearAccess ?? "",
  power: venueDetails?.power ?? "",
  lighting: venueDetails?.lighting ?? "",
  dressingRooms: venueDetails?.dressingRooms ?? "",
  noiseRestrictions: venueDetails?.noiseRestrictions ?? "",
})

export const convertToBankFormState = (bankDetails: server.dto.CompanyBank): BankFormState => ({
  accountNumber: bankDetails.accountNumber ?? "",
  beneficiaryName: bankDetails.beneficiaryName ?? "",
  swiftCode: bankDetails.swiftCode ?? "",
  sortCode: bankDetails.sortCode ?? "",
  bankAddress: bankDetails.bankAddress ?? "",
});

export const getInstruction = (
  companyForm: CompanyFormState,
  bankForm: BankFormState,
  partial: Partial<server.dto.UpdateCompanyInstruction>
): server.dto.UpdateCompanyInstruction => ({
  additional: companyForm.additional,
  administrativeAddress: {
    addressLine1: companyForm.address.postalAddress.addressLine1,
    addressLine2: companyForm.address.postalAddress.addressLine2,
    city: companyForm.address.postalAddress.city,
    postalCode: companyForm.address.postalAddress.postalCode,
    stateId: companyForm.address.postalAddress.state.key
  },
  regularAddress: {
    addressLine1: companyForm.address.visitingAddress.addressLine1,
    addressLine2: companyForm.address.visitingAddress.addressLine2,
    city: companyForm.address.visitingAddress.city,
    postalCode: companyForm.address.visitingAddress.postalCode,
    stateId: companyForm.address.visitingAddress.state.key
  },
  regularName: companyForm.address.visitingAddress.companyName,
  administrativeName: companyForm.address.postalAddress.companyName,
  bankDetails: {
    accountNumber: bankForm.accountNumber,
    beneficiaryName: bankForm.beneficiaryName,
    swiftCode: bankForm.swiftCode,
    sortCode: bankForm.sortCode,
    bankAddress: bankForm.bankAddress
  },
  businessRegistrationNumber: companyForm.businessRegistrationNumber,
  companyId: companyForm.companyId,
  euVatNumber: companyForm.euVatNumber.number,
  expectPurchaseOrderNumber: companyForm.expectPurchaseOrderNumber,
  facebook: companyForm.facebook,
  financialRisk: {
    comment: companyForm.financialRisk?.comment,
    level: companyForm.financialRisk?.level,
  },
  fiscalNumber1: companyForm.fiscalNumber1,
  fiscalNumber2: companyForm.fiscalNumber2,
  geoLocation: companyForm.geoCode?.location,
  googlePlaceIdentifier: companyForm.googlePlaceIdentifier,
  industryClassification: companyForm.industryClassification,
  internalId: companyForm.internalId,
  isArtist: companyForm.profile.isArtist,
  isPromoter: companyForm.profile.isPromoter,
  isVenue: companyForm.profile.isVenue,
  legalStructure: companyForm.legalStructure,
  networkPath: companyForm.networkPath,
  ownerUserId: companyForm.owner.userId,
  phoneNumbers: convertToPhoneNumbers(companyForm.phone),
  statusId: companyForm.status.statusId,
  website: companyForm.website,
  globalLocationNumber: companyForm.globalLocationNumber,
  ...partial,
});

export const getUpdateVenueInstuction = (companyId: number, venue: VenueFormState, partial: Partial<server.dto.SetVenueDetailsInstruction>): server.dto.SetVenueDetailsInstruction => ({
  companyId,
  ageRestriction: venue.ageRestriction?.value,
  capacity: venue.capacity,
  category: venue.category?.value,
  // @ts-ignore
  doors: venue.doors ? {
    open: venue.doors.open?.format("HH:mm"),
    close: venue.doors.close?.format("HH:mm")
  } : undefined,
  dressingRooms: venue.dressingRooms,
  gearAccess: venue.gearAccess,
  lighting: venue.lighting,
  noiseRestrictions: venue.noiseRestrictions,
  parking: venue.parking,
  power: venue.power,
  stage: venue.stage,
  vehicleAccess: venue.vehicleAccess,
  ...partial,
});

export const getUpdateBankAccountInstruction = (companyFormState: CompanyFormState, bankAccountFormState: BankFormState, partial: Partial<server.dto.UpdateBankAccountInstruction>): server.dto.UpdateCompanyInstruction => {
  const instruction = getInstruction(companyFormState, bankAccountFormState, {});

  return {
    ...instruction,
    bankDetails: {
      ...instruction.bankDetails,
      accountNumber: partial.accountNumber ?? instruction!.bankDetails!.accountNumber,
      bankAddress: partial.bankAddress ?? instruction!.bankDetails!.bankAddress,
      beneficiaryName: partial.beneficiaryName ?? instruction!.bankDetails!.beneficiaryName,
      sortCode: partial.sortCode ?? instruction!.bankDetails!.sortCode,
      swiftCode: partial.swiftCode ?? instruction!.bankDetails!.swiftCode,
    }
  }
};

export interface CompanyFieldMetaData {
    deleted: FieldMetaData;
    isValidated: FieldMetaData;

    visitingAddress: FieldMetaData;
    postalAddress: FieldMetaData;
    profile: FieldMetaData;
    status: FieldMetaData;
    tags: FieldMetaData;
    owner: FieldMetaData;
    localTime: FieldMetaData;

    businessCentral: FieldMetaData;
    hubSpot: FieldMetaData;

    financialRisk: FieldMetaData;
    euVatNumber: FinancialFieldMetaData;
    fiscalNumber1: FinancialFieldMetaData;
    fiscalNumber2: FinancialFieldMetaData;
    legalStructure: FinancialFieldMetaData;
    businessRegistrationNumber: FinancialFieldMetaData;
    globalLocationNumber: FieldMetaData;
    internalId: FieldMetaData;
    expectPurchaseOrderNumber: FieldMetaData;

    phone: FieldMetaData;
    website: FieldMetaData;
    facebook: FieldMetaData;
    networkPath: FieldMetaData;
    additionInformation: FieldMetaData;

    capacity: FieldMetaData;
    ageRestriction: FieldMetaData;
    doors: FieldMetaData;
    category: FieldMetaData;
    stage: FieldMetaData;
    vehicleAccess: FieldMetaData;
    parking: FieldMetaData;
    gearAccess: FieldMetaData;
    power: FieldMetaData;
    lighting: FieldMetaData;
    dressingRooms: FieldMetaData;
    noiseRestrictions: FieldMetaData;

    accountNumber: FieldMetaData;
    beneficiaryName: FieldMetaData;
    swiftCode: FieldMetaData;
    sortCode: FieldMetaData;
    bankAddress: FieldMetaData;

    map: FieldMetaData;
}

export const getCompanyFieldMetaData = (
    environment: server.dto.SelectedEnvironment,
    serverReference: server.dto.ServerReference,
    company: CompanyFormState,
    venue: VenueFormState,
    bank: BankFormState,
    itemPermissions: server.dto.CompanyObjectPermissions,
    hasTags: boolean,
    hasMultipleStatuses: boolean,
    displayBusinessCentralNumber: boolean,
    displayHubSpotPanel: boolean,
): CompanyFieldMetaData => {
    const formDefinition = getCountryFormDefinition(company.address.postalAddress.country.key, serverReference);

    return {
      deleted: createFieldMetaData(
        false,
        !company.isDeleted,
        true,
    ),

    isValidated: createFieldMetaData(
        false,
        company.isValidated,
        !itemPermissions.canModify,
    ),

    visitingAddress: createFieldMetaData(
        false,
        false,
        !itemPermissions.canModify,
    ),

    postalAddress: createFieldMetaData(
        false,
        false,
        environment.type === "external" || !itemPermissions.canModify,
    ),

    profile: createFieldMetaData(
        company.profile.isArtist === "no" && company.profile.isVenue === "no" && company.profile.isPromoter === "no",
        (company.profile.isArtist === "no" && company.profile.isVenue === "no" && company.profile.isPromoter === "no") && !itemPermissions.canModify,
        !itemPermissions.canModify,
    ),

    status: createFieldMetaData(
        false,
        (environment.type === "external" || !hasMultipleStatuses), //Dont display to external users
        !itemPermissions.canModify,
    ),

    tags: createFieldMetaData(
        !company.tags || company.tags.length === 0,
        !hasTags || environment.type === "external" || ((!company.tags || company.tags.length === 0) && !itemPermissions.canModify),
        !itemPermissions.canModify,
    ),

    owner: createFieldMetaData(
        false,
        environment.type === "external", //Dont display to external users,
        !itemPermissions.canModify,
    ),

    localTime: createFieldMetaData(
        !company.ianaTimeZoneId,
        environment.type === "external",
        true, //Cannot modify
    ),

    businessCentral: createFieldMetaData(
      false,
      !displayBusinessCentralNumber, //Hide when not displayed
      true, //Cannot modify
    ),

    hubSpot: createFieldMetaData(
      false,
      !displayHubSpotPanel, //Hide when not displayed
      true, //Cannot modify
    ),

    // Financial
    financialRisk: createFieldMetaData(
      !company.financialRisk || company.financialRisk.level === "unknown",
      false,
      !itemPermissions.canModify,
    ),

    euVatNumber: createFinancialFieldMetaData(
        formDefinition.euVatNumber.label,
        getMergefieldTitle(formDefinition.euVatNumber),
        formDefinition.euVatNumber.sublabel,
        !company.euVatNumber.number,
        !formDefinition.euVatNumber.displayInUI || (!company.euVatNumber.number && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    fiscalNumber1: createFinancialFieldMetaData(
        formDefinition.fiscalNumber1.label,
        getMergefieldTitle(formDefinition.fiscalNumber1),
        formDefinition.fiscalNumber1.sublabel,
        !company.fiscalNumber1,
        !formDefinition.fiscalNumber1.displayInUI || (!company.fiscalNumber1 && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    fiscalNumber2: createFinancialFieldMetaData(
        formDefinition.fiscalNumber2.label,
        getMergefieldTitle(formDefinition.fiscalNumber2),
        formDefinition.fiscalNumber2.sublabel,
        !company.fiscalNumber2,
        !formDefinition.fiscalNumber2.displayInUI || (!company.fiscalNumber2 && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    legalStructure: createFinancialFieldMetaData(
        formDefinition.legalStructure.label,
        getMergefieldTitle(formDefinition.legalStructure),
        formDefinition.legalStructure.sublabel,
        !company.legalStructure,
        !formDefinition.legalStructure.displayInUI || (!company.legalStructure && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    businessRegistrationNumber: createFinancialFieldMetaData(
        formDefinition.businessRegistrationNumber.label,
        getMergefieldTitle(formDefinition.businessRegistrationNumber),
        formDefinition.businessRegistrationNumber.sublabel,
        !company.businessRegistrationNumber,
        !formDefinition.businessRegistrationNumber.displayInUI || (!company.businessRegistrationNumber && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    globalLocationNumber: createFieldMetaData(
        !company.globalLocationNumber,
        (!company.globalLocationNumber && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    internalId: createFieldMetaData(
        !company.internalId,
        (!company.internalId && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    expectPurchaseOrderNumber: createFieldMetaData(
        false,
        !environment.purchaseOrderNumber,
        !itemPermissions.canModify,
    ),

    // Contact
    phone: createFieldMetaData(
        !company.phone,
        !company.phone && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    website: createFieldMetaData(
        !company.website,
        (!company.website && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    facebook: createFieldMetaData(
        !company.facebook,
        (!company.facebook && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    networkPath: createFieldMetaData(
        !company.networkPath,
        (!company.networkPath && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    additionInformation: createFieldMetaData(
        !company.additional,
        environment.type === "external" || (!company.additional && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    // Venue
    capacity: createFieldMetaData(
        !venue.capacity,
        !venue.capacity && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    ageRestriction: createFieldMetaData(
        !venue.ageRestriction || venue.ageRestriction.value === "unknown",
        (!venue.ageRestriction || venue.ageRestriction.value === "unknown") && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    doors: createFieldMetaData(
        !venue.doors || (!venue.doors.open && !venue.doors.close),
        (!venue.doors || (!venue.doors.open && !venue.doors.close)) && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    category: createFieldMetaData(
        !venue.category || venue.category.value === "unknown",
        (!venue.category || venue.category.value === "unknown") && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    stage: createFieldMetaData(
        !hasStageDimensions(venue.stage),
        !hasStageDimensions(venue.stage) && !itemPermissions.canModify, //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    vehicleAccess: createFieldMetaData(
        !venue.vehicleAccess,
        (!venue.vehicleAccess && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    parking: createFieldMetaData(
        !venue.parking,
        (!venue.parking && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    gearAccess: createFieldMetaData(
        !venue.gearAccess,
        (!venue.gearAccess && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    power: createFieldMetaData(
        !venue.power,
        (!venue.power && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    lighting: createFieldMetaData(
        !venue.lighting,
        (!venue.lighting && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    dressingRooms: createFieldMetaData(
        !venue.dressingRooms,
        (!venue.dressingRooms && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    noiseRestrictions: createFieldMetaData(
        !venue.noiseRestrictions,
        (!venue.noiseRestrictions && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    // Bank
    accountNumber: createFieldMetaData(
        !bank.accountNumber,
        (!bank.accountNumber && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    beneficiaryName: createFieldMetaData(
        !bank.beneficiaryName,
        !bank.accountNumber || (!bank.beneficiaryName && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    swiftCode: createFieldMetaData(
        !bank.swiftCode,
        !bank.accountNumber || (!bank.swiftCode && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    sortCode: createFieldMetaData(
        !bank.sortCode,
        !bank.accountNumber || (!bank.sortCode && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    bankAddress: createFieldMetaData(
        !bank.bankAddress,
        !bank.accountNumber || (!bank.bankAddress && !itemPermissions.canModify), //Hide when empty and read-only
        !itemPermissions.canModify,
    ),

    map: createFieldMetaData(
        !company.geoCode.location,
        !company.geoCode.location,
        false
    ),
  }
};

const getMergefieldTitle = (formDefinition: server.dto.FormElementDefinition) => `Mergefield: ${formDefinition.mergefield}`;
